/**
 * Workshop widget renderers — polished dashboard look (Recharts + Tailwind).
 */

import DOMPurify from "dompurify";
import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";
import { type ReactNode, useEffect, useId, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import type {
  BarChartWidget,
  HtmlWidget,
  LineChartWidget,
  ListWidget,
  MarkdownWidget,
  MetricWidget,
  TableColumn,
  TableWidget,
} from "./types";

const BAR_SWATCH = [
  "hsl(var(--primary))",
  "hsl(var(--clay))",
  "hsl(28 55% 52%)",
  "hsl(var(--lemon))",
  "hsl(16 45% 45%)",
];

function formatNumber(n: number, unit?: string): string {
  const formatted = n.toLocaleString();
  if (!unit) return formatted;
  if (unit === "¥" || unit === "$") return `${unit}${formatted}`;
  return `${formatted} ${unit}`;
}

function ChartShell({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="group relative h-full overflow-hidden rounded-xl border-border/60 bg-gradient-to-b from-card/95 to-card/80 shadow-md ring-1 ring-black/[0.04] backdrop-blur-sm dark:ring-white/[0.06]">
      <div className="pointer-events-none absolute -right-8 -top-12 h-32 w-32 rounded-full bg-primary/[0.11] blur-2xl transition-opacity group-hover:opacity-100" />
      <div className="pointer-events-none absolute -bottom-10 -left-10 h-28 w-28 rounded-full bg-lemon/[0.08] blur-2xl" />
      <CardHeader className="relative space-y-1 pb-1 pt-4">
        <CardTitle className="text-sm font-semibold tracking-tight">{title}</CardTitle>
        {hint ? (
          <CardDescription className="text-[11px] leading-snug">{hint}</CardDescription>
        ) : null}
      </CardHeader>
      <CardContent className="relative pb-4 pt-0">{children}</CardContent>
    </Card>
  );
}

function ChartTooltipBody({
  active,
  payload,
  label,
  unit,
}: {
  active?: boolean;
  payload?: ReadonlyArray<{ value?: number }>;
  label?: string;
  unit?: string;
}) {
  if (!active || !payload?.length) return null;
  const v = payload[0]?.value;
  if (typeof v !== "number") return null;
  return (
    <div className="rounded-lg border border-border/90 bg-popover/95 px-3 py-2 text-xs shadow-lg backdrop-blur-md">
      <div className="font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 font-semibold tabular-nums text-foreground">{formatNumber(v, unit)}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Metric — glassy hero stat
// ────────────────────────────────────────────────────────────────────

export function MetricCard({ widget }: { widget: MetricWidget }) {
  const delta = widget.delta;
  const positive = delta && delta.value > 0;
  const negative = delta && delta.value < 0;
  return (
    <Card className="relative h-full overflow-hidden rounded-xl border-border/60 bg-gradient-to-br from-card via-card to-primary/[0.06] shadow-md ring-1 ring-black/[0.04] dark:ring-white/[0.06]">
      <div className="pointer-events-none absolute right-3 top-3 rounded-full bg-primary/10 p-2 text-primary/80">
        <Sparkles className="h-4 w-4" />
      </div>
      <CardHeader className="space-y-0 pb-2 pr-14 pt-5">
        <CardDescription className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {widget.title}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 pb-5">
        <div className="flex flex-wrap items-baseline gap-2">
          <div className="bg-gradient-to-br from-foreground to-foreground/75 bg-clip-text text-3xl font-bold tracking-tight text-transparent tabular-nums">
            {widget.value}
          </div>
          {delta ? (
            <span
              className={cn(
                "inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold shadow-sm",
                positive && "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
                negative && "bg-red-500/15 text-red-700 dark:text-red-400",
                !positive && !negative && "bg-muted text-muted-foreground",
              )}
            >
              {positive ? (
                <ArrowUpRight className="h-3 w-3" />
              ) : negative ? (
                <ArrowDownRight className="h-3 w-3" />
              ) : (
                <ArrowRight className="h-3 w-3" />
              )}
              {Math.abs(delta.value).toLocaleString()}
              {delta.suffix ?? ""}
            </span>
          ) : null}
        </div>
        {delta?.label ? (
          <div className="text-[11px] font-medium text-muted-foreground">{delta.label}</div>
        ) : null}
        {widget.hint ? (
          <div className="text-[11px] leading-relaxed text-muted-foreground/85">{widget.hint}</div>
        ) : null}
      </CardContent>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────
// Bar chart — Recharts + gradient bars
// ────────────────────────────────────────────────────────────────────

export function BarChartCard({ widget }: { widget: BarChartWidget }) {
  const data = useMemo(
    () => widget.data.map((d) => ({ name: d.label, value: d.value })),
    [widget.data],
  );
  const many = data.length > 6;

  return (
    <ChartShell title={widget.title} hint={widget.hint}>
      <div className="h-[min(280px,42vw)] w-full min-h-[220px] min-w-0 sm:h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 6, right: 4, left: -8, bottom: many ? 16 : 6 }}
            barCategoryGap="18%"
          >
            <CartesianGrid
              strokeDasharray="4 4"
              vertical={false}
              stroke="hsl(var(--border))"
              strokeOpacity={0.85}
            />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
              tickLine={false}
              axisLine={{ stroke: "hsl(var(--border))" }}
              interval={0}
              angle={many ? -35 : 0}
              textAnchor={many ? "end" : "middle"}
              height={many ? 54 : 28}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
              tickLine={false}
              axisLine={false}
              width={36}
            />
            <Tooltip
              cursor={{ fill: "hsl(var(--muted) / 0.25)" }}
              content={(props) => (
                <ChartTooltipBody
                  active={props.active}
                  payload={props.payload as ReadonlyArray<{ value?: number }> | undefined}
                  label={props.label as string | undefined}
                  unit={widget.unit}
                />
              )}
            />
            <Bar dataKey="value" radius={[7, 7, 2, 2]} maxBarSize={52}>
              {data.map((d, i) => (
                <Cell key={`${d.name}-${i}`} fill={BAR_SWATCH[i % BAR_SWATCH.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartShell>
  );
}

// ────────────────────────────────────────────────────────────────────
// Line / area chart — smooth area + glow stroke
// ────────────────────────────────────────────────────────────────────

export function LineChartCard({ widget }: { widget: LineChartWidget }) {
  const gid = useId().replace(/:/g, "");
  const data = useMemo(() => widget.data.map((d) => ({ name: d.x, value: d.y })), [widget.data]);

  return (
    <ChartShell title={widget.title} hint={widget.hint}>
      <div className="h-[min(280px,42vw)] w-full min-h-[220px] min-w-0 sm:h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 8, left: -8, bottom: 4 }}>
            <defs>
              <linearGradient id={`${gid}-area`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.45} />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="4 4"
              vertical={false}
              stroke="hsl(var(--border))"
              strokeOpacity={0.85}
            />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
              tickLine={false}
              axisLine={{ stroke: "hsl(var(--border))" }}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
              tickLine={false}
              axisLine={false}
              width={36}
            />
            <Tooltip
              content={(props) => (
                <ChartTooltipBody
                  active={props.active}
                  payload={props.payload as ReadonlyArray<{ value?: number }> | undefined}
                  label={props.label as string | undefined}
                  unit={widget.unit}
                />
              )}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke="hsl(var(--primary))"
              strokeWidth={2.5}
              fill={`url(#${gid}-area)`}
              dot={{
                r: 4,
                fill: "hsl(var(--card))",
                stroke: "hsl(var(--primary))",
                strokeWidth: 2,
              }}
              activeDot={{ r: 6, strokeWidth: 0, fill: "hsl(var(--primary))" }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </ChartShell>
  );
}

// ────────────────────────────────────────────────────────────────────
// Editable table — CRUD on rows, persisted up via onChange
// ────────────────────────────────────────────────────────────────────

interface TableCardProps {
  widget: TableWidget;
  onChange?: (next: TableWidget) => void;
}

export function TableCard({ widget, onChange }: TableCardProps) {
  // Local copy so editing-as-you-type doesn't yo-yo when the parent
  // batches saves. Sync to incoming `widget.rows` if the spec changes
  // out from under us (e.g. switch page).
  const [rows, setRows] = useState(widget.rows);
  useEffect(() => {
    setRows(widget.rows);
  }, [widget.rows]);

  const editable = onChange !== undefined;

  const commit = (next: Record<string, string | number>[]): void => {
    setRows(next);
    onChange?.({ ...widget, rows: next });
  };

  return (
    <Card className="h-full overflow-hidden rounded-xl border-border/60 bg-card/90 shadow-md ring-1 ring-black/[0.04] backdrop-blur-[2px] dark:ring-white/[0.06]">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div className="space-y-1">
          <CardTitle className="text-[13px]">{widget.title}</CardTitle>
          {widget.hint ? (
            <CardDescription className="text-[11px]">{widget.hint}</CardDescription>
          ) : null}
        </div>
        {editable ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              const blank: Record<string, string | number> = { id: crypto.randomUUID() };
              for (const col of widget.columns) {
                blank[col.key] = col.kind === "number" ? 0 : "";
              }
              commit([...rows, blank]);
            }}
            className="h-7 px-2 text-[11px]"
          >
            <Plus className="mr-1 h-3 w-3" />
            新增行
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="px-0 pb-2">
        <div className="overflow-auto rounded-lg border border-border/50 bg-muted/20">
          <Table>
            <TableHeader>
              <TableRow className="border-border/60 bg-muted/40 hover:bg-muted/40">
                {widget.columns.map((col) => (
                  <TableHead key={col.key} className="text-[11px] uppercase tracking-[0.06em]">
                    {col.label}
                  </TableHead>
                ))}
                {editable ? <TableHead className="w-10" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={widget.columns.length + (editable ? 1 : 0)}
                    className="text-center text-[12px] text-muted-foreground"
                  >
                    没有数据。点"新增行"添加一条。
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, rowIdx) => (
                  <RowEditor
                    key={(row.id as string | undefined) ?? `row-${rowIdx}`}
                    columns={widget.columns}
                    row={row}
                    editable={editable}
                    zebra={rowIdx % 2 === 1}
                    onChange={(next) => {
                      const copy = rows.slice();
                      copy[rowIdx] = next;
                      commit(copy);
                    }}
                    onDelete={() => {
                      const copy = rows.slice();
                      copy.splice(rowIdx, 1);
                      commit(copy);
                    }}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function RowEditor({
  columns,
  row,
  editable,
  zebra,
  onChange,
  onDelete,
}: {
  columns: TableColumn[];
  row: Record<string, string | number>;
  editable: boolean;
  zebra?: boolean;
  onChange: (next: Record<string, string | number>) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  return (
    <TableRow className={cn("border-border/40", zebra && "bg-muted/25")}>
      {columns.map((col) => {
        const value = row[col.key];
        const isEditable = editable && col.editable !== false;
        if (editing && isEditable) {
          return (
            <TableCell key={col.key} className="p-1">
              <Input
                autoFocus={col.key === columns[0].key}
                type={col.kind === "number" ? "number" : "text"}
                value={value === undefined || value === null ? "" : String(value)}
                onChange={(e) => {
                  const raw = e.target.value;
                  onChange({
                    ...row,
                    [col.key]: col.kind === "number" ? (raw === "" ? 0 : Number(raw)) : raw,
                  });
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") setEditing(false);
                  if (e.key === "Escape") setEditing(false);
                }}
                className="h-7 text-[12px]"
              />
            </TableCell>
          );
        }
        return (
          <TableCell key={col.key} className="text-[12px]">
            {col.kind === "tag" ? (
              <Badge variant="secondary" className="text-[10px]">
                {String(value ?? "—")}
              </Badge>
            ) : col.kind === "number" ? (
              <span className="tabular-nums">
                {typeof value === "number" ? value.toLocaleString() : (value ?? "—")}
              </span>
            ) : (
              (value ?? "—")
            )}
          </TableCell>
        );
      })}
      {editable ? (
        <TableCell className="text-right">
          <div className="flex justify-end gap-0.5">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => setEditing((v) => !v)}
              aria-label="编辑这一行"
              className="h-7 w-7"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={onDelete}
              aria-label="删除这一行"
              className="h-7 w-7 text-destructive hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </TableCell>
      ) : null}
    </TableRow>
  );
}

// ────────────────────────────────────────────────────────────────────
// List + markdown
// ────────────────────────────────────────────────────────────────────

export function ListCard({ widget }: { widget: ListWidget }) {
  return (
    <Card className="h-full overflow-hidden rounded-xl border-border/60 bg-gradient-to-b from-card to-muted/15 shadow-md ring-1 ring-black/[0.04] dark:ring-white/[0.06]">
      <CardHeader className="space-y-1 pb-2">
        <CardTitle className="text-sm font-semibold">{widget.title}</CardTitle>
        {widget.hint ? (
          <CardDescription className="text-[11px] leading-snug">{widget.hint}</CardDescription>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3 pb-5">
        {widget.items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border/60 py-8 text-center text-[12px] text-muted-foreground">
            暂无条目
          </div>
        ) : (
          widget.items.map((item, i) => (
            <div
              key={`${item.title}-${i}`}
              className="relative overflow-hidden rounded-lg border border-border/50 bg-card/80 p-3 pl-4 shadow-sm transition-colors hover:bg-muted/20"
            >
              <div className="absolute bottom-0 left-0 top-0 w-1 rounded-l-lg bg-gradient-to-b from-primary to-clay" />
              <div className="flex items-start justify-between gap-2 pl-1">
                <div className="text-[13px] font-semibold leading-snug tracking-tight">
                  {item.title}
                </div>
                {item.tag ? (
                  <Badge variant="secondary" className="shrink-0 text-[10px] font-medium shadow-sm">
                    {item.tag}
                  </Badge>
                ) : null}
              </div>
              {item.body ? (
                <div className="mt-2 pl-1 text-[12px] leading-relaxed text-muted-foreground">
                  {item.body}
                </div>
              ) : null}
              {item.meta ? (
                <div className="mt-2 pl-1 text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground/90">
                  {item.meta}
                </div>
              ) : null}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

export function MarkdownCard({ widget }: { widget: MarkdownWidget }) {
  const blocks = useMemo(() => parseLite(widget.body), [widget.body]);
  return (
    <Card className="h-full overflow-hidden rounded-xl border-border/60 bg-card/95 shadow-md ring-1 ring-black/[0.04] dark:ring-white/[0.06]">
      {widget.title ? (
        <CardHeader className="space-y-0 pb-2 pt-5">
          <CardTitle className="text-sm font-semibold">{widget.title}</CardTitle>
        </CardHeader>
      ) : null}
      <CardContent className="prose-claude max-w-none space-y-2 px-6 pb-6 pt-2 text-[13px]">
        {blocks.map((block) => {
          if (block.kind === "heading") {
            return (
              <p
                key={block.key}
                className="text-xs font-bold uppercase tracking-[0.14em] text-primary/90"
              >
                {block.text}
              </p>
            );
          }
          if (block.kind === "list") {
            return (
              <ul key={block.key} className="list-disc space-y-0.5 pl-4">
                {block.items.map((it, i) => (
                  <li key={`${block.key}-${i}`}>{renderInline(it)}</li>
                ))}
              </ul>
            );
          }
          return <p key={block.key}>{renderInline(block.text)}</p>;
        })}
      </CardContent>
    </Card>
  );
}

export function sanitizeWorkshopHtmlFragment(raw: string): string {
  return DOMPurify.sanitize(raw, {
    ADD_TAGS: ["details", "summary"],
    ADD_ATTR: ["target", "rel", "colspan", "rowspan", "open"],
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: [
      "script",
      "iframe",
      "object",
      "embed",
      "form",
      "style",
      "link",
      "meta",
      "base",
      "textarea",
      "input",
    ],
    FORBID_ATTR: ["style"],
  });
}

/**
 * Rich HTML block — **native** browser interactivity where safe:
 * links (`<a href>`), `<details>` / `<summary>` toggles, tables, etc.
 * Inline `style`, event handlers, scripts, and iframes are stripped.
 */
export function HtmlCard({ widget }: { widget: HtmlWidget }) {
  const safe = useMemo(() => sanitizeWorkshopHtmlFragment(widget.html), [widget.html]);
  return (
    <Card className="h-full overflow-hidden rounded-xl border-border/60 bg-card/95 shadow-md ring-1 ring-black/[0.04] dark:ring-white/[0.06]">
      {widget.title ? (
        <CardHeader className="space-y-0 pb-2 pt-5">
          <CardTitle className="text-sm font-semibold">{widget.title}</CardTitle>
        </CardHeader>
      ) : null}
      <CardContent className="px-6 pb-6 pt-2">
        <div
          className="workshop-html prose-claude max-w-none text-[13px] leading-relaxed"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized via DOMPurify in sanitizeWorkshopHtmlFragment.
          dangerouslySetInnerHTML={{ __html: safe }}
        />
      </CardContent>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────
// Markdown parsing (lite)
// ────────────────────────────────────────────────────────────────────

type Block =
  | { kind: "p"; text: string; key: string }
  | { kind: "heading"; text: string; key: string }
  | { kind: "list"; items: string[]; key: string };

function parseLite(src: string): Block[] {
  const out: Block[] = [];
  const lines = src.split("\n");
  let buffer: string[] | null = null;
  let counter = 0;
  const flushBuffer = (): void => {
    if (buffer && buffer.length > 0) {
      out.push({ kind: "list", items: buffer, key: `b-${counter++}-list` });
    }
    buffer = null;
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushBuffer();
      continue;
    }
    if (line.startsWith("- ")) {
      if (!buffer) buffer = [];
      buffer.push(line.slice(2));
      continue;
    }
    flushBuffer();
    if (line.startsWith("**") && line.endsWith("**")) {
      out.push({ kind: "heading", text: line.slice(2, -2), key: `b-${counter++}-h` });
      continue;
    }
    out.push({ kind: "p", text: line, key: `b-${counter++}-p` });
  }
  flushBuffer();
  return out;
}

/** Render ``**bold**`` and ``_italic_`` as proper React nodes — no
 *  ``dangerouslySetInnerHTML`` so we never have to think about XSS for
 *  user-authored markdown. */
function renderInline(text: string): ReactNode[] {
  const tokens: ReactNode[] = [];
  const re = /\*\*(.+?)\*\*|_(.+?)_/g;
  let lastIndex = 0;
  let key = 0;
  for (let m = re.exec(text); m !== null; m = re.exec(text)) {
    if (m.index > lastIndex) {
      tokens.push(text.slice(lastIndex, m.index));
    }
    if (m[1] !== undefined) {
      tokens.push(<strong key={`tok-${key++}`}>{m[1]}</strong>);
    } else if (m[2] !== undefined) {
      tokens.push(<em key={`tok-${key++}`}>{m[2]}</em>);
    }
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) {
    tokens.push(text.slice(lastIndex));
  }
  return tokens;
}
